import React, {useCallback, useEffect, useState} from 'react'
import {AuthModal, useAuthModal} from '../../hooks/use-auth-modal'
import PropTypes from 'prop-types'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import {useIntl} from 'react-intl'
import {useLocation} from 'react-router-dom'
import {useCustomToast} from '../../contexts/custom-toast-context'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useBrand} from '../../contexts/brand-context'

/**
 * Higher-order component that modifies the given component's `onClick` to show the login form if
 * the user is not logged in.
 * @param {React.Component} component Component to wrap.
 * @param {(location: Location, locale: string) => boolean} [options.isLoginPage] Function that
 * determines whether the current page is the "login" page, to avoid showing a duplicate modal.
 * Defaults to all paths ending with "/login".
 * @param {(location: Location, locale: string) => boolean} [options.isRegistrationPage] Function that
 * determines whether the current page is the "registration" page, to avoid showing a duplicate modal.
 * Defaults to all paths ending with "/registration".
 * @param {boolean} [options.isWishlistAction] Flag to indicate this is a wishlist action, which will
 * show the wishlist message in the auth modal.
 * @returns {React.Component} wrapped component
 */
const withRegistration = (
    Component,
    {
        isLoginPage = (loc) => loc.pathname.endsWith('/login'),
        isRegistrationPage = (loc) => loc.pathname.endsWith('/registration'),
        isWishlistAction = false
    } = {}
) => {
    const WrappedComponent = ({onClick = noop, ...passThroughProps}) => {
        const {data: customer} = useCurrentCustomer()
        const authModal = useAuthModal()
        const {showCustomToast} = useCustomToast()
        const location = useLocation()
        const {formatMessage, locale} = useIntl()
        const [pendingAction, setPendingAction] = useState(false)
        const wishlistProductId =
            passThroughProps?.wishlistProductId ||
            passThroughProps?.['data-wishlist-product-id'] ||
            passThroughProps?.productId
        const pendingWishlistKey = 'pendingWishlistProductId'
        const {isGarage} = useBrand()

        const runPendingAction = useCallback(() => {
            if (!pendingAction) return
            if (isWishlistAction) {
                // For wishlist actions, wait until customerId is available so add-to-wishlist can succeed.
                return
            }
            setPendingAction(false)
            onClick()
        }, [pendingAction, onClick, isWishlistAction])

        useEffect(() => {
            if (!pendingAction || !customer?.isRegistered) return

            if (isWishlistAction) {
                // Wishlist replay is centralized by PendingWishlistReplay to avoid
                // duplicate triggers across multiple withRegistration instances.
                setPendingAction(false)
                return
            }

            runPendingAction()
        }, [
            pendingAction,
            customer?.isRegistered,
            isWishlistAction,
            runPendingAction
        ])

        const handleClick = (e) => {
            e.preventDefault()

            if (!customer?.isRegistered) {
                if (isWishlistAction && wishlistProductId && typeof window !== 'undefined') {
                    window.sessionStorage.setItem(
                        pendingWishlistKey,
                        String(wishlistProductId)
                    )
                }
                setPendingAction(true)
                if (isLoginPage(location, locale)) {
                    const message = isGarage
                        ? formatMessage({
                              id: 'with_registration.info.please_sign_in.garage',
                              defaultMessage: 'Please sign in to continue!'
                          })
                        : formatMessage({
                              id: 'with_registration.info.please_sign_in.dynamite',
                              defaultMessage: 'Please sign in to continue!'
                          })

                    showCustomToast({message})
                } else if (isRegistrationPage(location, locale)) {
                    const message = isGarage
                        ? formatMessage({
                              id: 'with_registration.info.already_on_registration.garage',
                              defaultMessage: 'You are already on the registration page.'
                          })
                        : formatMessage({
                              id: 'with_registration.info.already_on_registration.dynamite',
                              defaultMessage: 'You are already on the registration page.'
                          })

                    showCustomToast({message})
                } else {
                    // Set the wishlist flag if this is a wishlist action
                    if (isWishlistAction) {
                        authModal.setIsWishlistClick(true)
                    }
                    authModal.onOpen()
                }
            } else {
                onClick()
            }
        }

        return (
            <React.Fragment>
                <Component {...passThroughProps} onClick={handleClick} />
                <AuthModal
                    {...authModal}
                    onLoginSuccess={runPendingAction}
                    onRegistrationSuccess={runPendingAction}
                />
            </React.Fragment>
        )
    }

    WrappedComponent.propTypes = {
        onClick: PropTypes.func
    }

    return WrappedComponent
}

export default withRegistration
